import { sentryAddAppLoadBreadcrumb, sentryCaptureAppLoadDiagnostic } from '@/utils/sentry';

export const AppBundle = {
  Journeys: 'journeys',
  DesignStudio: 'design-studio',
} as const;

export type AppBundle = (typeof AppBundle)[keyof typeof AppBundle];

export const AppBundleRoutes = {
  [AppBundle.Journeys]: '/journeys',
  [AppBundle.DesignStudio]: '/design-studio',
};

export const AppBundleNames = {
  [AppBundle.Journeys]: 'Journeys',
  [AppBundle.DesignStudio]: 'Design Studio',
};

declare global {
  interface Window {
    __hydra_preload_app_bundles__?: {
      [key in AppBundle]: Promise<unknown> | undefined;
    };
  }
}

export function getImportShim() {
  if (window.importShim == null) {
    throw new Error('es-module-shims did not initialize window.importShim.');
  }

  return window.importShim;
}

export const registerPreloadPromise = (bundle: AppBundle, callback: () => Promise<unknown>) => {
  window.__hydra_preload_app_bundles__ ??= {
    [AppBundle.Journeys]: undefined,
    [AppBundle.DesignStudio]: undefined,
  };

  if (window.__hydra_preload_app_bundles__[bundle] != null) {
    sentryAddAppLoadBreadcrumb('preload promise already registered', { appBundle: bundle });
    return;
  }

  const startedAt = performance.now();
  sentryAddAppLoadBreadcrumb('preload promise registered', { appBundle: bundle });

  window.__hydra_preload_app_bundles__[bundle] = callback()
    .then((entrypoint) => {
      sentryAddAppLoadBreadcrumb('preload promise resolved', {
        appBundle: bundle,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      return entrypoint;
    })
    .catch((error: unknown) => {
      sentryAddAppLoadBreadcrumb(
        'preload promise rejected',
        {
          appBundle: bundle,
          elapsedMs: Math.round(performance.now() - startedAt),
        },
        'error',
      );
      sentryCaptureAppLoadDiagnostic(
        'preload_rejected',
        {
          appBundle: bundle,
          elapsedMs: Math.round(performance.now() - startedAt),
          errorName: error instanceof Error ? error.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        { level: 'error' },
      );
      throw error;
    });
};

export const getEntrypoint = async (bundle: AppBundle) => {
  const preloadPromise = window.__hydra_preload_app_bundles__?.[bundle];
  if (preloadPromise == null) {
    const startedAt = performance.now();
    sentryAddAppLoadBreadcrumb('entrypoint import started', { appBundle: bundle, source: 'importShim' });

    try {
      const entrypoint = await getImportShim()(bundle);
      sentryAddAppLoadBreadcrumb('entrypoint import resolved', {
        appBundle: bundle,
        source: 'importShim',
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      return entrypoint;
    } catch (error) {
      sentryAddAppLoadBreadcrumb(
        'entrypoint import rejected',
        {
          appBundle: bundle,
          source: 'importShim',
          elapsedMs: Math.round(performance.now() - startedAt),
        },
        'error',
      );
      sentryCaptureAppLoadDiagnostic(
        'entrypoint_import_rejected',
        {
          appBundle: bundle,
          elapsedMs: Math.round(performance.now() - startedAt),
          errorName: error instanceof Error ? error.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        { level: 'error' },
      );
      throw error;
    }
  }

  sentryAddAppLoadBreadcrumb('entrypoint awaiting preload promise', { appBundle: bundle });
  return await preloadPromise;
};
